/**
 * AI Scribe V2 — transcript bubble
 *
 * Provider lines sit on the left lane, patient lines on the right, each carrying
 * the speaker name and a wall-clock stamp — the dialogue reading of the mock.
 *
 * @author [Rajkumar Rathod]
 */

import { RefObject } from "react";
import Flex from "../../../../../../../../../../components/Flex";
import Text from "../../../../../../../../../../components/Text";
import {
  PRIMITIVE,
  px,
} from "../../../../../../../../../../tokens/design-tokens";
import { SCRIBE_V2_COLORS, SCRIBE_V2_DIMENSIONS } from "../constants";
import { ScribeTranscriptLine } from "../types";

interface TranscriptBubbleProps {
  line: ScribeTranscriptLine;
  /** Attached to the newest bubble so the panel can auto-scroll to it */
  anchorRef?: RefObject<HTMLDivElement>;
}

const TranscriptBubble = ({ line, anchorRef }: TranscriptBubbleProps) => {
  const isPatient = line.role === "PATIENT";

  return (
    <div ref={anchorRef} style={{ width: "100%" }}>
      <Flex width="100%" justifyContent={isPatient ? "flex-end" : "flex-start"}>
        <Flex
          flexDirection="column"
          gap={0.25}
          px={px(PRIMITIVE.spacing[12])}
          py={px(PRIMITIVE.spacing[8])}
          sx={{
            maxWidth: "82%",
            borderRadius: SCRIBE_V2_DIMENSIONS.bubbleRadius,
            backgroundColor: SCRIBE_V2_COLORS.bubbleBg,
          }}
        >
          <Flex alignItems="center" justifyContent="space-between" gap={2}>
            <Text
              sx={{
                color: isPatient
                  ? SCRIBE_V2_COLORS.bubblePatientName
                  : SCRIBE_V2_COLORS.bubbleProviderName,
                fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
                fontWeight: PRIMITIVE.typography.fontWeight.semiBold,
              }}
            >
              {line.speaker}
            </Text>
            <Text
              sx={{
                color: SCRIBE_V2_COLORS.textMuted,
                fontSize: px(PRIMITIVE.typography.fontSize.body.b5),
                whiteSpace: "nowrap",
              }}
            >
              {line.timestamp}
            </Text>
          </Flex>

          <Text
            sx={{
              color: SCRIBE_V2_COLORS.textPrimary,
              fontSize: px(PRIMITIVE.typography.fontSize.body.b4),
              lineHeight: px(PRIMITIVE.typography.lineHeight["150%"][14]),
            }}
          >
            {line.text}
          </Text>
        </Flex>
      </Flex>
    </div>
  );
};

export default TranscriptBubble;
